import React from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaDownload,
  FaListAlt,
  FaPlay,
  FaRedo,
  FaStream
} from "react-icons/fa";
import styled from "styled-components";

const FeaturesContainer = styled.main`
  background: linear-gradient(to bottom, #f5fbff, #ffffff);
  padding: 112px 20px 0;

  @media (max-width: 768px) {
    padding: 88px 16px 0;
  }
`;

const HeroSection = styled.section`
  margin: 0 auto 36px;
  max-width: 820px;
  text-align: center;

  h1 {
    background: linear-gradient(90deg, #0078d4 0%, #00a88f 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: clamp(2.2rem, 6vw, 3.6rem);
    line-height: 1.08;
    margin: 0 0 18px;
  }

  p {
    color: #526174;
    font-size: 1.12rem;
    line-height: 1.7;
    margin: 0 auto;
    max-width: 720px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
`;

const PrimaryLink = styled(Link)`
  background: #0078d4;
  border: 2px solid #0078d4;
  border-radius: 999px;
  color: white;
  font-weight: 800;
  padding: 11px 22px;
  text-decoration: none;

  &:hover {
    background: #0069ba;
    border-color: #0069ba;
    color: white;
  }
`;

const SecondaryLink = styled(Link)`
  background: white;
  border: 2px solid #0078d4;
  border-radius: 999px;
  color: #0078d4;
  font-weight: 800;
  padding: 11px 22px;
  text-decoration: none;

  &:hover {
    background: #edf7ff;
    color: #0069ba;
  }
`;

const FeatureSection = styled.section`
  margin: 0 auto;
  max-width: 1180px;
  padding: 54px 0;
`;

const SectionTitle = styled.h2`
  color: #172033;
  font-size: clamp(1.8rem, 4vw, 2.45rem);
  margin: 0 0 10px;
  text-align: center;
`;

const SectionSubtitle = styled.p`
  color: #64748b;
  line-height: 1.65;
  margin: 0 auto 28px;
  max-width: 680px;
  text-align: center;
`;

const FeatureGrid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;

const FeatureCard = styled.article`
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.07);
  padding: 24px;

  h3 {
    color: #172033;
    font-size: 1.28rem;
    margin: 14px 0 8px;
  }

  p {
    color: #64748b;
    line-height: 1.6;
    margin: 0 0 16px;
  }
`;

const FeatureIcon = styled.div`
  align-items: center;
  background: #edf7ff;
  border-radius: 14px;
  color: #0078d4;
  display: flex;
  font-size: 1.55rem;
  height: 52px;
  justify-content: center;
  width: 52px;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FeatureItem = styled.li`
  align-items: flex-start;
  color: #475569;
  display: flex;
  font-size: 0.94rem;
  gap: 8px;
  margin-top: 8px;

  svg {
    color: #00a88f;
    flex: 0 0 auto;
    margin-top: 3px;
  }
`;

const Note = styled.div`
  background: #edf7ff;
  border: 1px solid #c7e8ff;
  border-radius: 16px;
  color: #3d5368;
  line-height: 1.65;
  margin: 0 auto;
  max-width: 880px;
  padding: 20px 22px;
  text-align: center;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #0078d4, #00a88f);
  color: white;
  margin: 42px -20px 0;
  padding: 54px 20px;
  text-align: center;

  h2 {
    font-size: clamp(1.8rem, 4vw, 2.4rem);
    margin: 0 0 10px;
  }

  p {
    line-height: 1.6;
    margin: 0 auto;
    max-width: 620px;
    opacity: 0.92;
  }
`;

const WhiteLink = styled(Link)`
  background: white;
  border-radius: 999px;
  color: #0078d4;
  display: inline-block;
  font-weight: 800;
  margin-top: 22px;
  padding: 12px 24px;
  text-decoration: none;
`;

export const Rundoit = () => {
  return (
    <FeaturesContainer>
      <HeroSection>
        <h1>A simpler way to choose what to do next</h1>
        <p>
          Rundoit turns a task list into a clear next action. Add your tasks,
          pick the mode that fits your day, and spend less energy deciding
          where to begin.
        </p>
        <ButtonRow>
          <PrimaryLink to="/demo">Try the Interactive Demo</PrimaryLink>
          <SecondaryLink to="/signup">Start Free</SecondaryLink>
        </ButtonRow>
      </HeroSection>

      <FeatureSection>
        <SectionTitle>Three ways to get moving</SectionTitle>
        <SectionSubtitle>
          Choose a mode based on the kind of focus you need right now.
        </SectionSubtitle>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon><span aria-hidden="true">🎲</span></FeatureIcon>
            <h3>Random Mode</h3>
            <p>
              When your list feels overwhelming, let Rundoit pick one task for
              you. You get a single main task to focus on without reshuffling
              your list again.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Pick one task from your list</FeatureItem>
              <FeatureItem><FaCheckCircle /> Roll again when you need a different task</FeatureItem>
              <FeatureItem><FaCheckCircle /> Reduce decision fatigue</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaPlay /></FeatureIcon>
            <h3>Start Mode</h3>
            <p>
              Work through your list in order with a 25-minute focus session.
              When the timer ends, take a break and continue with the next task.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Start with the first task on your list</FeatureItem>
              <FeatureItem><FaCheckCircle /> Use a built-in 25-minute focus timer</FeatureItem>
              <FeatureItem><FaCheckCircle /> Continue to the next task after a break</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaStream /></FeatureIcon>
            <h3>Flow Mode</h3>
            <p>
              Use an open-ended timer for deep work that should not be boxed
              into a fixed session. Pause for a break and resume when you are
              ready.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Use an open-ended focus timer</FeatureItem>
              <FeatureItem><FaCheckCircle /> Pause and resume for breaks</FeatureItem>
              <FeatureItem><FaCheckCircle /> Stay with one highlighted task</FeatureItem>
            </FeatureList>
          </FeatureCard>
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection>
        <SectionTitle>Tools for your real task list</SectionTitle>
        <SectionSubtitle>
          Start with a few tasks, keep the ones that matter, and add structure
          when your day needs it.
        </SectionSubtitle>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon><FaListAlt /></FeatureIcon>
            <h3>Task List and Templates</h3>
            <p>
              Add your own tasks or choose a starter template for common
              routines such as a morning routine, study plan, or workday flow.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Add and delete tasks quickly</FeatureItem>
              <FeatureItem><FaCheckCircle /> Start from a ready-made task template</FeatureItem>
              <FeatureItem><FaCheckCircle /> Save your list to your account</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaRedo /></FeatureIcon>
            <h3>Keep Important Tasks</h3>
            <p>
              Mark a task as Keep when it should remain on your list after it
              has been selected. This is useful for routines and tasks you want
              to revisit.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Keep regular tasks after selection</FeatureItem>
              <FeatureItem><FaCheckCircle /> Keep calendar tasks after selection</FeatureItem>
              <FeatureItem><FaCheckCircle /> Reset your current mode at any time</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaCheckCircle /></FeatureIcon>
            <h3>Completed Task History</h3>
            <p>
              Finishing a task moves it into a visible history instead of
              deleting it. Undo immediately, restore it later, archive it, or
              choose permanent deletion yourself.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> See when each task was completed</FeatureItem>
              <FeatureItem><FaCheckCircle /> Undo or restore completed work</FeatureItem>
              <FeatureItem><FaCheckCircle /> Keep archive and delete as separate actions</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaCalendarAlt /></FeatureIcon>
            <h3>Built-In Calendar Planning</h3>
            <p>
              Add tasks to dates in the Rundoit calendar, link an existing
              task, or create a new calendar task when planning your week.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> View tasks on a monthly calendar</FeatureItem>
              <FeatureItem><FaCheckCircle /> Link calendar entries to tasks</FeatureItem>
              <FeatureItem><FaCheckCircle /> Include calendar tasks in your focus modes</FeatureItem>
            </FeatureList>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon><FaDownload /></FeatureIcon>
            <h3>Calendar Export</h3>
            <p>
              Download your Rundoit calendar as a standard ICS file when you
              want to bring those planned tasks into another calendar app.
            </p>
            <FeatureList>
              <FeatureItem><FaCheckCircle /> Export events as an ICS file</FeatureItem>
              <FeatureItem><FaCheckCircle /> Use a standard calendar format</FeatureItem>
              <FeatureItem><FaCheckCircle /> Access Rundoit from your web browser</FeatureItem>
            </FeatureList>
          </FeatureCard>
        </FeatureGrid>
      </FeatureSection>

      <Note>
        Free accounts can save up to 5 tasks. Paid plans increase the task
        limit, and the interactive demo is available without an account.
      </Note>

      <CTASection>
        <h2>Try the useful part before signing up</h2>
        <p>
          Add a task, press Random, and see whether having one clear next
          action makes your list feel lighter.
        </p>
        <WhiteLink to="/demo">Open the Demo</WhiteLink>
      </CTASection>
    </FeaturesContainer>
  );
};
